// BB-130-CB.5 - Importar socketService para callback
import socketService from './socketService';
import { API_URL } from '../config/api';

/**
 * Serviço de síntese de voz que usa o Google Text-to-Speech via API do backend
 */
class SpeechService {
  constructor() {
    // Verificar se a Web Speech API está disponível como fallback
    this.webSpeechAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window;
    
    // API do backend para gTTS (centralizada)
    this.backendApiUrl = API_URL;
    this.gttsEndpoint = `${this.backendApiUrl}/senhas/sintetizar-voz`;
    
    // Armazenar as mensagens já processadas para cache
    this.audioCache = new Map();
    
    // Configurar uso prioritário de gTTS
    this.useGTTS = true;
    this.gttsFalhas = 0;
    this.maxGTTSFalhas = 3;
    
    // console.log('Serviço de voz inicializado');
    // console.log('Usando gTTS como opção principal');
    
    // Estado de autorização do usuário
    this.autorizado = this.verificarAutorizacao();
    
    // Fila de vocalizações
    this.filaVocalizacoes = [];
    this.vocalizandoAtualmente = false;
    this.ultimaVocalizacao = Date.now();
    this.timeoutSeguranca = null;
    
    // Período de segurança entre vocalizações (ms)
    this.delayEntreVocalizacoes = 1000;
    this.tempoPausaAposErro = 3000;
    
    // ==========================================
    // BB-130-CB.5 - CONTROLE DE CALLBACK
    // ==========================================
    
    // Contexto da vocalização atual para callback
    this.vocalizacaoAtual = null;
    
    // Carregar vozes do Web Speech API como fallback
    if (this.webSpeechAvailable) {
      this.setupWebSpeechFallback();
    }
    
    // Iniciar verificador periódico para detectar travamentos
    this.setupVerificadorTravamento();
  }
  
  // Configura o fallback para Web Speech API
  setupWebSpeechFallback() {
    // Carregar lista de vozes quando disponíveis
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = this.loadVoices.bind(this);
    }
    
    // Tentar carregar as vozes imediatamente também
    this.loadVoices();
    
    // Fix para navegadores Chrome que às vezes param de falar
    if ('webkitSpeechSynthesis' in window) {
      this.setupChromeFix();
    }
  }
  
  // Verificador periódico otimizado para detectar e corrigir travamentos
  setupVerificadorTravamento() {
    setInterval(() => {
      const agora = Date.now();
      const tempoDecorrido = agora - this.ultimaVocalizacao;
      
      // Detecção de travamento mais precisa (10s ao invés de 15s)
      if (this.vocalizandoAtualmente && tempoDecorrido > 10000) {
        console.warn(`🚨 Travamento detectado na vocalização. Última atividade: ${Math.round(tempoDecorrido/1000)}s atrás`);
        
        try {
          // Cancelar síntese atual se estiver usando Web Speech API
          if (window.speechSynthesis && window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
            console.log('🔄 Web Speech API cancelada');
          }
          
          // Reset de estado confiável
          this.vocalizandoAtualmente = false;
          this.limparTimeoutSeguranca();
          
          console.log('🔄 Estado resetado, reativando processamento em 2s');
          
          // Fazer uma pausa antes de tentar a próxima vocalização
          setTimeout(() => {
            this.processarProximaDaFila();
          }, 2000);
          
        } catch (error) {
          console.error('❌ Erro ao tentar recuperar de travamento:', error);
        }
      }
      
      // Lógica inteligente para detectar itens presos na fila (>5s)
      if (!this.vocalizandoAtualmente && this.filaVocalizacoes.length > 0) {
        const itemMaisAntigo = this.filaVocalizacoes[0];
        const tempoNaFila = agora - itemMaisAntigo.timestamp;
        
        if (tempoNaFila > 5000) {
          console.log(`🔄 Item preso na fila há ${Math.round(tempoNaFila/1000)}s. Reativando processamento...`);
          this.processarProximaDaFila();
        }
      }
    }, 3000);
  }
  
  /**
   * Verifica se o usuário autorizou a reprodução de áudio
   */
  verificarAutorizacao() {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('audio_autorizado') === 'true';
  }
  
  /**
   * Define autorização para vocalização
   */
  setAutorizacao(autorizado) {
    this.autorizado = autorizado;
    localStorage.setItem('audio_autorizado', autorizado);
    return this.autorizado;
  }
  
  /**
   * Implementa uma solução para o Chrome que às vezes para de falar após 15 segundos
   */
  setupChromeFix() {
    // Chrome tem um bug que para a síntese de voz após alguns segundos
    // Esta é uma solução temporária que mantém a síntese "viva"
    setInterval(() => {
      if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }
    }, 10000);
  }
  
  /**
   * Carrega as vozes disponíveis para o Web Speech API (fallback)
   */
  loadVoices() {
    if (!window.speechSynthesis) return;
    
    try {
      this.voices = window.speechSynthesis.getVoices();
      this.voicesLoaded = this.voices.length > 0;
      
      // Registrar no console para debug
      if (this.voicesLoaded) {
        // console.log(`Vozes carregadas para fallback: ${this.voices.length} disponíveis`);
      } else {
        // Tentar novamente em 100ms, às vezes leva um tempo
        setTimeout(() => {
          this.voices = window.speechSynthesis.getVoices();
          this.voicesLoaded = this.voices.length > 0;
          // console.log(`Retry: ${this.voices.length} vozes carregadas`);
        }, 100);
      }
    } catch (error) {
      console.error('Erro ao carregar vozes:', error);
    }
  }
  
  /**
   * Encontra a melhor voz em português para o fallback
   */
  getPortugueseVoice() {
    // Garantir que as vozes foram carregadas
    if (!this.voicesLoaded || this.voices.length === 0) {
      this.loadVoices();
    }
    
    // Procurar por vozes em português
    let ptVoices = this.voices.filter(voice => 
      voice.lang.startsWith('pt') || 
      voice.lang.startsWith('pt-BR')
    );
    
    // Selecionar a primeira voz em português encontrada
    if (ptVoices.length > 0) {
      return ptVoices[0];
    }
    
    // Se não encontrar vozes em português, tentar espanhol (mais próximo)
    let esVoices = this.voices.filter(voice => 
      voice.lang.startsWith('es')
    );
    
    if (esVoices.length > 0) {
      return esVoices[0];
    }
    
    // Se não encontrar, retornar qualquer voz disponível
    return this.voices[0] || null;
  }

  /**
   * Método simplificado para vocalizar uma senha (fire-and-forget)
   * Interface simplificada que retorna imediatamente e gerencia a fila internamente
   * @param {string} numero - Número da senha
   * @param {number} guiche - Número do guichê
   * @param {boolean} preferencial - Se é senha preferencial
   * @param {number} prioridade - Prioridade da vocalização (0 = normal, >0 = alta)
   * @param {number} senhaId - ID da senha no banco (BB-130-CB.8)
   * @returns {boolean} - true se foi adicionada à fila, false se rejeitada
   */
  vocalizarSenha(numero, guiche, preferencial = false, prioridade = 0, senhaId = null) {
    if (!this.autorizado) {
      console.warn('🔇 Tentativa de vocalizar senha, mas vocalização não está autorizada');
      return false;
    }
    
    const id = `senha_${numero}_guiche_${guiche}_${Date.now()}`;
    
    // Verificar se esta senha já está na fila
    const jaNaFila = this.filaVocalizacoes.some(
      item => item.numero === numero && item.guiche === guiche
    );
    
    if (jaNaFila) {
      console.log(`🔄 Senha ${numero} guichê ${guiche} já está na fila. Ignorando duplicação.`);
      return false;
    }
    
    // Criar o item da fila
    const item = {
      id,
      numero,
      guiche,
      preferencial,
      prioridade,
      senhaId, // BB-130-CB.8 - Adicionar senhaId para callback
      timestamp: Date.now()
    };
    
    // Adicionar à fila com base na prioridade
    if (prioridade > 0) {
      // Inserir no início da fila para prioridade alta
      this.filaVocalizacoes.unshift(item);
    } else {
      // Adicionar ao final da fila para prioridade normal
      this.filaVocalizacoes.push(item);
    }
    
    console.log(`🎵 Senha ${numero} guichê ${guiche} adicionada à fila. Total: ${this.filaVocalizacoes.length}`);
    
    // Se não estiver vocalizando, iniciar processamento
    if (!this.vocalizandoAtualmente) {
      this.processarProximaDaFila();
    }
    
    return true;
  }
  
  /**
   * Processa o próximo item da fila de vocalização
   * Versão otimizada com timeout reduzido e delays inteligentes
   */
  processarProximaDaFila() {
    // Se não há itens na fila ou já está vocalizando, sair
    if (this.filaVocalizacoes.length === 0 || this.vocalizandoAtualmente) {
      return;
    }
    
    // Marcar como em andamento
    this.vocalizandoAtualmente = true;
    
    // Registrar timestamp
    this.ultimaVocalizacao = Date.now();
    
    // Pegar o primeiro item da fila
    const item = this.filaVocalizacoes.shift();
    
    console.log(`🎵 Processando vocalização: Senha ${item.numero} Guichê ${item.guiche}`);
    
    // BB-130-CB.5 - Armazenar contexto da vocalização atual
    this.vocalizacaoAtual = {
      senhaId: item.senhaId,
      numero: item.numero,
      guiche: item.guiche,
      preferencial: item.preferencial,
      iniciadaEm: Date.now()
    };
    
    // ==========================================
    // BB-130-CB.9 - SISTEMA DE FALLBACK FRONTEND
    // ==========================================
    
    // Timeout de segurança no frontend (12 segundos)
    const timeoutSegurancaFrontend = setTimeout(() => {
      console.warn(`⏰ [FRONTEND] Timeout de segurança atingido para senha ${item.numero}, forçando conclusão`);
      
      // Forçar conclusão da vocalização se ainda estiver em andamento
      if (this.vocalizandoAtualmente && this.vocalizacaoAtual) {
        this.continuarProcessamento(false); // Considerar como sucesso para não reenviar
      }
    }, 12000);
    
    // Armazenar o timeout para poder cancelá-lo
    this.timeoutSegurancaFrontend = timeoutSegurancaFrontend;
    
    // Chamar o método speak com os parâmetros e timeout reduzido
    this.speak('', {
      numero: item.numero,
      guiche: item.guiche,
      preferencial: item.preferencial,
      senhaId: item.senhaId, // BB-130-CB.8 - Passar senhaId para callback
      bypass: true, // Bypass a verificação de autorização pois já verificamos
      timeout: 8000 // Timeout reduzido para 8 segundos
    })
    .then(result => {
      console.log(`✅ Vocalização concluída para senha ${item.numero}`);
      
      // Continuar processamento após sucesso
      this.continuarProcessamento();
    })
    .catch(error => {
      console.error(`❌ Erro ao vocalizar senha ${item.numero}:`, error);
      
      // Continuar processamento após erro
      this.continuarProcessamento(true);
    });
  }

  /**
   * Centraliza lógica de continuidade da fila após conclusão ou erro de vocalização
   * Implementa delays inteligentes baseados no tamanho da fila
   * @param {boolean} houveFalha - Se houve falha na vocalização anterior
   */
  continuarProcessamento(houveFalha = false) {
    // ==========================================
    // BB-130-CB.5 - EMITIR CALLBACK DE CONCLUSÃO
    // ==========================================
    
    // Emitir callback de conclusão da vocalização se temos contexto
    if (this.vocalizacaoAtual && this.vocalizacaoAtual.senhaId) {
      const tempoVocalizacao = Date.now() - this.vocalizacaoAtual.iniciadaEm;
      
      console.log(`🔊 [CALLBACK] Emitindo callback de conclusão para senha ${this.vocalizacaoAtual.senhaId}, tempo: ${tempoVocalizacao}ms, sucesso: ${!houveFalha}`);
      
      socketService.notificarVocalizacaoConcluida(
        this.vocalizacaoAtual.senhaId,
        !houveFalha, // sucesso = !houveFalha
        tempoVocalizacao
      );
      
      // Limpar contexto da vocalização
      this.vocalizacaoAtual = null;
    }
    
    // BB-130-CB.9 - Cancelar timeout de segurança do frontend
    if (this.timeoutSegurancaFrontend) {
      clearTimeout(this.timeoutSegurancaFrontend);
      this.timeoutSegurancaFrontend = null;
    }
    
    // Marcar como não vocalizando
    this.vocalizandoAtualmente = false;
    
    // Calcular delay inteligente baseado no tamanho da fila e se houve falha
    let delay;
    
    if (houveFalha) {
      // Delay maior após erro
      delay = 2000;
      console.log('🔄 Aguardando 2s antes de continuar (após erro)');
    } else if (this.filaVocalizacoes.length > 3) {
      // Fila grande: delay menor para processar mais rápido
      delay = 500;
      console.log('🔄 Aguardando 500ms antes de continuar (fila > 3 itens)');
    } else {
      // Fila pequena: delay normal
      delay = 1000;
      console.log('🔄 Aguardando 1s antes de continuar (fila ≤ 3 itens)');
    }
    
    // Aguardar o delay antes de processar a próxima
    setTimeout(() => {
      this.processarProximaDaFila();
    }, delay);
  }
  
  /**
   * Limpa o timeout de segurança
   */
  limparTimeoutSeguranca() {
    if (this.timeoutSeguranca) {
      clearTimeout(this.timeoutSeguranca);
      this.timeoutSeguranca = null;
    }
  }

  /**
   * Fala o texto usando o serviço gTTS do backend ou Web Speech API como fallback
   * @param {string} texto - O texto a ser falado (ou vazio se usar params de senha/guichê)
   * @param {Object} options - Opções adicionais
   * @returns {Promise<Object>} - Objeto de controle da fala
   */
  async speak(texto, options = {}) {
    // Atualizar o estado de autorização
    this.autorizado = this.verificarAutorizacao();
    
    console.log(`🎤 Tentando vocalizar${options.numero ? ` senha ${options.numero} guichê ${options.guiche}` : ''}`);
    
    // Verificar se o usuário autorizou a reprodução de áudio
    if (!this.autorizado && !options.bypass) {
      console.warn('🔇 Tentativa de vocalização sem autorização do usuário');
      return Promise.reject(new Error('Vocalização não autorizada pelo usuário'));
    }
    
    // Configurar timeout de segurança (personalizado ou padrão)
    const timeoutDuration = options.timeout || 12000;
    const timeoutPromise = new Promise((_, reject) => {
      this.timeoutSeguranca = setTimeout(() => {
        reject(new Error('⏰ Timeout de segurança: vocalização demorou muito tempo'));
      }, timeoutDuration);
    });
    
    try {
      // Se gTTS estiver habilitado e tiver número e guichê, usar gTTS
      if (this.useGTTS && options.numero && options.guiche) {
        return await Promise.race([
          this.speakWithGTTS(texto, options),
          timeoutPromise
        ]);
      }
      
      // Fallback para Web Speech API
      return await Promise.race([
        this.speakWithWebSpeech(texto || 'Teste de vocalização', options),
        timeoutPromise
      ]);
    } catch (error) {
      console.error("❌ Erro ao usar serviço primário de voz:", error);
      
      // Incrementar contador de falhas
      if (this.useGTTS) {
        this.gttsFalhas++;
        
        // Se excedeu o número máximo de falhas, desabilitar gTTS temporariamente
        if (this.gttsFalhas >= this.maxGTTSFalhas) {
          console.warn(`⚠️ gTTS falhou ${this.gttsFalhas} vezes. Desabilitando temporariamente.`);
          this.useGTTS = false;
          
          // Reabilitar após 1 minuto
          setTimeout(() => {
            console.log('🔄 Reativando gTTS após período de penalidade');
            this.useGTTS = true;
            this.gttsFalhas = 0;
          }, 60000);
        }
      }
      
      // Se falhar com gTTS, tentar Web Speech API
      if (this.webSpeechAvailable) {
        console.log("🔄 Tentando fallback para Web Speech API");
        try {
          this.limparTimeoutSeguranca();
          return await this.speakWithWebSpeech(texto || `Senha ${options.numero} Guichê ${options.guiche}`, options);
        } catch (fallbackError) {
          console.error("❌ Erro no fallback:", fallbackError);
          throw fallbackError;
        }
      }
      
      throw new Error('Nenhum serviço de síntese de voz disponível');
    } finally {
      this.limparTimeoutSeguranca();
    }
  }

  /**
   * Usa a API de gTTS do backend para falar o texto
   * @param {string} texto - O texto a ser falado
   * @param {Object} options - Opções adicionais
   * @returns {Promise<Object>} - Objeto de controle da fala
   */
  async speakWithGTTS(texto, options = {}) {
    let audioElement = null;
    let audioUrl = null;
    
    return new Promise(async (resolve, reject) => {
      try {
        // Extrair possíveis parâmetros para gTTS
        const { numero, guiche, preferencial } = options;
        
        // Se temos uma senha e guichê específicos
        if (numero && guiche) {
          console.log(`Vocalizando senha ${numero} guichê ${guiche} com gTTS`);
          
          // Verificar se já temos o áudio em cache
          const cacheKey = `senha_${numero}_guiche_${guiche}_pref_${preferencial ? 1 : 0}`;
          
          if (this.audioCache.has(cacheKey)) {
            console.log('Usando áudio em cache');
            // Usar clone para evitar problemas com reprodução simultânea
            audioElement = this.audioCache.get(cacheKey).cloneNode(true);
          } else {
            console.log('Gerando novo áudio para senha:', numero, 'guichê:', guiche);
            // Fazer a requisição para o backend
            try {
              const response = await fetch(this.gttsEndpoint, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                  numero, 
                  guiche,
                  preferencial: preferencial || false
                })
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Erro na API gTTS: ${response.status} ${errorText}`);
              }
              
              // Criar blob e URL do áudio
              const audioBlob = await response.blob();
              audioUrl = URL.createObjectURL(audioBlob);
              
              // Criar elemento de áudio
              audioElement = new Audio(audioUrl);
              
              // Verificar se o áudio foi carregado corretamente
              if (audioBlob.size === 0 || audioBlob.size < 1000) {
                throw new Error('Áudio gerado com tamanho suspeito, possível falha na síntese');
              }
              
              // Armazenar em cache para uso futuro
              console.log('Armazenando áudio em cache para senha:', numero, 'guichê:', guiche);
              this.audioCache.set(cacheKey, audioElement);
            } catch (fetchError) {
              console.error('Erro ao buscar áudio do backend:', fetchError);
              throw fetchError;
            }
          }
        } else {
          // Texto genérico, usar Web Speech API
          throw new Error('Sem parâmetros de senha e guichê. Usando fallback.');
        }
        
        // Verificar se o áudio está pronto
        if (!audioElement || !audioElement.src) {
          throw new Error('Elemento de áudio não foi criado corretamente');
        }
        
        console.log('Preparando reprodução do áudio...');
        
        // Configurar eventos ANTES de iniciar a reprodução
        audioElement.onended = () => {
          console.log('Vocalização concluída com sucesso');
          
          // Resetar contador de falhas de gTTS em caso de sucesso
          this.gttsFalhas = 0;
          
          resolve({
            succeeded: true,
            play: () => {
              audioElement.currentTime = 0;
              return audioElement.play();
            },
            pause: () => audioElement.pause(),
            resume: () => audioElement.play(),
            stop: () => {
              audioElement.pause();
              audioElement.currentTime = 0;
            },
            dispose: () => {
              audioElement.pause();
              if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
              }
            }
          });
        };
        
        // Configurar outros eventos importantes
        audioElement.onerror = (event) => {
          console.error('Erro ao reproduzir áudio:', event);
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
          }
          reject(new Error('Erro ao reproduzir áudio: ' + (event.message || 'Erro desconhecido')));
        };
        
        // Evento para quando o áudio está carregado e pronto para reproduzir
        audioElement.oncanplaythrough = () => {
          console.log('Áudio carregado e pronto para reprodução');
        };
        
        // Configurar timeout de segurança para caso o evento onended não dispare
        const safetyTimeout = setTimeout(() => {
          // Se este timeout executar, significa que o onended não disparou
          console.log('Timeout de segurança acionado para finalizar a vocalização');
          
          // Resolver a promise - para não travar o fluxo
          if (audioElement) {
            audioElement.pause();
            audioElement.currentTime = 0;
          }
          
          resolve({
            succeeded: true,
            forced: true, // Indicador de que foi forçado por timeout
            play: () => {
              if (audioElement) {
                audioElement.currentTime = 0;
                return audioElement.play();
              }
              return Promise.resolve();
            },
            pause: () => { if (audioElement) audioElement.pause(); },
            resume: () => { if (audioElement) audioElement.play(); },
            stop: () => {
              if (audioElement) {
                audioElement.pause();
                audioElement.currentTime = 0;
              }
            },
            dispose: () => {
              if (audioElement) audioElement.pause();
              if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
              }
            }
          });
        }, 10000); // 10 segundos - tempo máximo que uma vocalização deveria durar
        
        // Adicionar um listener para limpar o timeout quando o áudio terminar
        audioElement.addEventListener('ended', () => {
          clearTimeout(safetyTimeout);
        });
        
        // Iniciar reprodução
        try {
          console.log('Iniciando reprodução do áudio...');
          await audioElement.play();
          console.log('Reprodução iniciada com sucesso');
        } catch (playError) {
          console.error('Erro ao iniciar reprodução:', playError);
          clearTimeout(safetyTimeout); // Limpar o timeout em caso de erro
          
          // Em caso de erro autoplay, tentar uma abordagem diferente
          reject(new Error('Erro ao iniciar reprodução: ' + playError.message));
        }
      
      } catch (error) {
        console.error('Erro geral ao usar gTTS:', error);
        // Limpar recursos em caso de erro
        if (audioElement) {
          audioElement.pause();
        }
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
        reject(error);
      }
    });
  }

  /**
   * Fala o texto usando a Web Speech API como fallback
   * @param {string} texto - O texto a ser falado
   * @param {Object} options - Opções adicionais
   * @returns {Promise<Object>} - Objeto de controle da fala
   */
  speakWithWebSpeech(texto, options = {}) {
    return new Promise((resolve, reject) => {
      try {
        // Se não tiver suporte ao Web Speech API no navegador
        if (!this.webSpeechAvailable) {
          console.warn("Web Speech API não disponível neste navegador");
          reject(new Error("Web Speech API não disponível"));
          return;
        }

        // Cancelar qualquer síntese anterior
        window.speechSynthesis.cancel();
        
        // Criar um novo objeto de fala
        const utterance = new SpeechSynthesisUtterance(texto);
        
        // Configurar o objeto de fala
        utterance.lang = options.lang || 'pt-BR';
        utterance.rate = options.rate || 0.9;
        utterance.pitch = options.pitch || 1;
        utterance.volume = options.volume || 1;
        
        // Tentar selecionar uma voz em português
        const portugueseVoice = this.getPortugueseVoice();
        if (portugueseVoice) {
          utterance.voice = portugueseVoice;
          console.log(`Usando voz: ${portugueseVoice.name} (${portugueseVoice.lang})`);
        } else {
          console.warn('Nenhuma voz adequada encontrada');
        }
        
        // Configurar eventos
        utterance.onend = () => {
          console.log('Vocalização concluída com sucesso');
          resolve({
            succeeded: true,
            play: () => {
              window.speechSynthesis.cancel();
              window.speechSynthesis.speak(utterance);
            },
            pause: () => window.speechSynthesis.pause(),
            resume: () => window.speechSynthesis.resume(),
            stop: () => window.speechSynthesis.cancel(),
            dispose: () => window.speechSynthesis.cancel()
          });
        };
        
        utterance.onerror = (event) => {
          console.error(`Erro na síntese de voz: ${event.error}`);
          reject(new Error(`Erro na síntese de voz: ${event.error}`));
        };
        
        // Inicializar a síntese com um pequeno atraso para garantir melhor compatibilidade
        setTimeout(() => {
          // Falar a mensagem
          window.speechSynthesis.speak(utterance);
          
          // Retornar objeto de controle
          const controller = {
            utterance,
            play: () => {
              window.speechSynthesis.cancel();
              window.speechSynthesis.speak(utterance);
            },
            pause: () => window.speechSynthesis.pause(),
            resume: () => window.speechSynthesis.resume(),
            stop: () => window.speechSynthesis.cancel(),
            dispose: () => window.speechSynthesis.cancel()
          };
          
          resolve(controller);
        }, 50);
      } catch (error) {
        console.error('Erro ao iniciar síntese de voz:', error);
        reject(error);
      }
    });
  }
  
  /**
   * Limpa o cache de áudio
   */
  clearCache() {
    this.audioCache.forEach((audio, key) => {
      try {
        if (audio.src && audio.src.startsWith('blob:')) {
          URL.revokeObjectURL(audio.src);
        }
      } catch (e) {
        console.warn(`Erro ao limpar cache para ${key}:`, e);
      }
    });
    this.audioCache.clear();
    console.log('Cache de áudio limpo');
  }
  
  /**
   * Obter status completo da fila de vocalização
   * @returns {Object} - Status detalhado da fila
   */
  getFilaStatus() {
    const agora = Date.now();
    const tempoUltimaVocalizacao = agora - this.ultimaVocalizacao;
    
    // Próximas 3 senhas na fila
    const proximasSenhas = this.filaVocalizacoes.slice(0, 3).map(item => ({
      senha: item.numero,
      guiche: item.guiche,
      preferencial: item.preferencial,
      tempoNaFila: Math.round((agora - item.timestamp) / 1000)
    }));
    
    return {
      tamanhoFila: this.filaVocalizacoes.length,
      vocalizandoAtualmente: this.vocalizandoAtualmente,
      tempoUltimaVocalizacao: Math.round(tempoUltimaVocalizacao / 1000),
      proximasSenhas,
      estadoAtual: this.vocalizandoAtualmente ? 'VOCALIZANDO' : 
                   this.filaVocalizacoes.length > 0 ? 'AGUARDANDO' : 'INATIVO',
      autorizado: this.autorizado,
      useGTTS: this.useGTTS,
      gttsFalhas: this.gttsFalhas,
      cacheSize: this.audioCache.size
    };
  }

  /**
   * Obter status do serviço (método legado mantido para compatibilidade)
   */
  getStatus() {
    return {
      autorizado: this.autorizado,
      webSpeechAvailable: this.webSpeechAvailable,
      useGTTS: this.useGTTS,
      gttsFalhas: this.gttsFalhas,
      filaLength: this.filaVocalizacoes.length,
      vocalizandoAtualmente: this.vocalizandoAtualmente,
      cacheSize: this.audioCache.size
    };
  }
}

// Exporta uma instância única do serviço
const speechService = new SpeechService();

// ==========================================
// BB-130-CB.12 - COMANDO DE DEBUG EXPANDIDO
// ==========================================

// Comando global para debug em produção
if (typeof window !== 'undefined') {
  window.debugVocalizacao = () => {
    const status = speechService.getFilaStatus();
    
    console.log('\n🎤 ===== DEBUG SISTEMA DE VOCALIZAÇÃO (BB-130-CB) =====');
    console.log(`📊 Estado Atual: ${status.estadoAtual}`);
    console.log(`📋 Tamanho da Fila: ${status.tamanhoFila}`);
    console.log(`⏱️  Última Vocalização: ${status.tempoUltimaVocalizacao}s atrás`);
    console.log(`🔊 Autorizado: ${status.autorizado ? '✅' : '❌'}`);
    console.log(`🌐 Usando gTTS: ${status.useGTTS ? '✅' : '❌'}`);
    console.log(`❌ Falhas gTTS: ${status.gttsFalhas}`);
    console.log(`💾 Cache Size: ${status.cacheSize}`);
    
    // BB-130-CB.12 - Informações do sistema de callback
    console.log('\n🔄 === SISTEMA DE CALLBACK ===');
    if (speechService.vocalizacaoAtual) {
      const tempoDecorrido = Date.now() - speechService.vocalizacaoAtual.iniciadaEm;
      console.log(`🎵 Vocalização Atual:`);
      console.log(`   - Senha ID: ${speechService.vocalizacaoAtual.senhaId || 'N/A'}`);
      console.log(`   - Número: ${speechService.vocalizacaoAtual.numero}`);
      console.log(`   - Guichê: ${speechService.vocalizacaoAtual.guiche}`);
      console.log(`   - Tempo Decorrido: ${Math.round(tempoDecorrido / 1000)}s`);
      console.log(`   - Preferencial: ${speechService.vocalizacaoAtual.preferencial ? '✅' : '❌'}`);
    } else {
      console.log(`🎵 Nenhuma vocalização em andamento`);
    }
    
    // Verificar conexão WebSocket
    console.log(`🔌 WebSocket Conectado: ${socketService.isConnected() ? '✅' : '❌'}`);
    
    if (status.proximasSenhas.length > 0) {
      console.log('\n📋 Próximas Senhas na Fila:');
      console.table(status.proximasSenhas);
    } else {
      console.log('\n📋 Fila vazia');
    }
    
    console.log('==========================================\n');
    
    return {
      ...status,
      vocalizacaoAtual: speechService.vocalizacaoAtual,
      websocketConectado: socketService.isConnected()
    };
  };
}

export default speechService; 